import { useState, useEffect, useRef } from 'react';

interface FAQItem {
  pergunta: string;
  resposta: string;
}

const faqData: FAQItem[] = [
  {
    pergunta: 'O que é o Diário do Eu do Futuro?',
    resposta: 'O Diário do Eu do Futuro é uma ferramenta exclusiva de autoconhecimento que combina técnicas de visualização criativa, journaling terapêutico e reprogramação mental. Você receberá um caderno físico premium com prompts guiados, exercícios de reflexão profunda e espaço para registrar sua jornada de transformação. Inclui também acesso digital a templates editáveis e áudios de meditação específicos para cada seção do diário.'
  },
  {
    pergunta: 'Como funciona a entrega e o acesso às experiências complementares?',
    resposta: 'Após a compra, você recebe acesso imediato ao conteúdo digital (áudios, templates, guias em PDF) por e-mail e na área de membros. O Kit Físico Premium é enviado via Correios com código de rastreamento em até 3 dias úteis após a confirmação do pagamento. Os Áudios Premium ficam disponíveis para download ilimitado e streaming na plataforma.'
  },
  {
    pergunta: 'Qual a diferença entre experiências complementares e exercícios avulsos?',
    resposta: 'Os exercícios avulsos são práticas específicas focadas em um tema (amor, prosperidade, saúde, etc.) com duração de 7-21 dias. Já as experiências complementares são ferramentas contínuas e mais abrangentes que você pode usar durante toda sua jornada. Enquanto os exercícios têm início, meio e fim, as experiências complementares são recursos permanentes que potencializam qualquer plano ou exercício que você esteja fazendo.'
  },
  {
    pergunta: 'Os Áudios Premium de Reprogramação funcionam mesmo?',
    resposta: 'Sim! Os áudios utilizam técnicas cientificamente comprovadas de hipnose ericksoniana, PNL (Programação Neurolinguística), frequências binaurais e afirmações positivas. São gravados por profissionais certificados em hipnoterapia e neurociência aplicada. Mais de 2.300 usuários relatam resultados significativos em mudança de crenças limitantes, redução de ansiedade e aumento de foco em até 21 dias de uso consistente.'
  },
  {
    pergunta: 'Posso usar as experiências complementares com qualquer plano?',
    resposta: 'Absolutamente! As experiências complementares foram desenvolvidas para potencializar qualquer plano, desde o Semente do Futuro até o Private. Elas funcionam de forma independente e também se integram perfeitamente aos exercícios dos planos. Muitos membros do Clube Eu do Futuro utilizam o Diário e os Áudios Premium como parte de sua rotina mensal.'
  },
  {
    pergunta: 'Como acesso o conteúdo após a compra?',
    resposta: 'Imediatamente após a confirmação do pagamento, você recebe um e-mail de boas-vindas com login e senha para a área de membros exclusiva. Lá você encontra todos os seus áudios, templates digitais, guias em PDF e pode acompanhar o rastreamento do Kit Físico. O acesso é vitalício e você pode baixar todo o conteúdo digital quantas vezes quiser.'
  },
  {
    pergunta: 'Qual o prazo de entrega do Kit Físico Premium?',
    resposta: 'O Kit Físico Premium é enviado em até 3 dias úteis após a confirmação do pagamento. O prazo de entrega varia conforme sua região: capitais e regiões metropolitanas recebem em 5-7 dias úteis, demais localidades em 7-12 dias úteis. Você recebe o código de rastreamento por e-mail e pode acompanhar em tempo real. Enviamos para todo o Brasil via Correios com seguro.'
  },
  {
    pergunta: 'Em quais formatos estão disponíveis os Áudios Premium?',
    resposta: 'Os Áudios Premium estão disponíveis em MP3 de alta qualidade (320kbps) para download ilimitado e também em streaming direto na plataforma. Você pode ouvir no celular, tablet, computador ou qualquer dispositivo. Cada áudio tem versões com música de fundo (frequências 432Hz e 528Hz) e versão sem música, para você escolher sua preferência.'
  },
  {
    pergunta: 'Como usar o Diário do Eu do Futuro no dia a dia?',
    resposta: 'Recomendamos dedicar 15-20 minutos diários, preferencialmente pela manhã ou antes de dormir. O diário está estruturado em 4 pilares: Autoconhecimento, Visualização do Futuro, Gratidão Diária e Plano de Ação. Você pode seguir a ordem sugerida ou pular para a seção que mais ressoa com seu momento atual. Inclui prompts guiados para facilitar a escrita e exercícios práticos semanais.'
  },
  {
    pergunta: 'Existe política de reembolso para as experiências complementares?',
    resposta: 'Sim! Oferecemos garantia incondicional de 30 dias para todas as experiências complementares. Se por qualquer motivo você não ficar satisfeito, basta solicitar o reembolso total dentro deste prazo. Para o Kit Físico Premium, você pode ficar com o material físico recebido, devolvemos 100% do valor investido. Nossa taxa de reembolso é inferior a 2%, pois confiamos na qualidade do conteúdo.'
  }
];

export default function FAQExperiencias() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(faqData.length).fill(false));
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = itemRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleItems((prev) => {
                const newVisible = [...prev];
                newVisible[index] = true;
                return newVisible;
              });
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full mb-6">
            <i className="ri-question-line text-amber-600 text-lg"></i>
            <span className="text-sm font-medium text-amber-900">Perguntas Frequentes</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Dúvidas Sobre as Experiências
          </h2>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Tudo o que você precisa saber sobre nossas ferramentas exclusivas de transformação
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqData.map((item, index) => {
            const isOpen = openIndex === index;
            const isVisible = visibleItems[index];
            const animationDirection = index % 2 === 0 ? 'left' : 'right';

            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`bg-white border border-gray-200 rounded-2xl overflow-hidden transition-all duration-700 hover:border-amber-300 hover:shadow-lg ${
                  isVisible
                    ? 'opacity-100 translate-x-0'
                    : `opacity-0 ${animationDirection === 'left' ? '-translate-x-8' : 'translate-x-8'}`
                }`}
                style={{
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-amber-50/30 transition-colors duration-300 group"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-100 to-yellow-100 rounded-full flex-shrink-0 group-hover:from-amber-200 group-hover:to-yellow-200 transition-all duration-300">
                      <span className="text-amber-700 font-bold text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {index + 1}
                      </span>
                    </div>
                    
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 pr-4 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {item.pergunta}
                    </h3>
                  </div>

                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-2xl text-amber-600"></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-8 pb-6 pl-[88px]">
                    <div className="border-l-2 border-amber-200 pl-6">
                      <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {item.resposta}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Footer */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-4 px-8 py-6 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl">
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full">
              <i className="ri-customer-service-2-line text-2xl text-white"></i>
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Ainda tem dúvidas?
              </h3>
              <p className="text-gray-600 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Nossa equipe está pronta para ajudar você
              </p>
              <a
                href="mailto:contato@eudofuturo.com.br"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-full hover:from-amber-700 hover:to-yellow-700 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <i className="ri-mail-line text-lg"></i>
                Falar com Suporte
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
